import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { signOut } from '@/lib/auth/sign-out';
import { toast } from '@/hooks/ui/use-toast';
import { Moon, Sun, Menu, User, ArrowRight, LogOut, Star, Heart, Mail, Users, Briefcase, BarChart3, Database, ChevronDown, Building2, Settings, UserCircle, Monitor, Bot, Bell, CheckCheck, Trash2, Plus, RotateCcw, X, Layers, Map, FileText } from 'lucide-react';
import { CollapseChevron } from '@/components/ui/collapsible';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useEmployee } from '@/context/EmployeeContext';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { NotificationCenter } from '@/components/notifications';
import { useNotificationContext } from '@/context/NotificationContext';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { useAccountSettings } from '@/context/AccountSettingsContext';
import { useBusinessSettings } from '@/context/BusinessSettingsContext';
import { AccountSettingsHoverMenu, BusinessSettingsHoverMenu } from '@/components/layout/SettingsHoverMenu';
import { businessSettingsPath } from '@/lib/settings-navigation';
import { MobileMenuPageContent } from '@/components/layout/MobileMenuPageContent';
import { useAIView } from '@/context/AIViewContext';
import { cn } from '@/lib/core/utils';
import { SimpleTooltip } from '@/components/overlays/simple-tooltip';
import { NotificationFilters } from '@/components/notifications/NotificationFilters';
import { NotificationList } from '@/components/notifications/NotificationList';
import { Separator } from '@/components/ui/separator';
import { useWizardDrafts } from '@/hooks/business/useWizardDrafts';
import { useSubscription } from '@/context/SubscriptionContext';
import { canOpenPortfolio } from '@/lib/constants/dashboard';

interface NavbarProps {
  businesses?: Array<{id: string, name: string, favicon_url?: string | null}>;
  selectedBusinessId?: string | null;
  onBusinessChange?: (businessId: string) => void;
  loadingBusinesses?: boolean;
  hideBusinessNavigation?: boolean;
  forceShowBusinessSelect?: boolean;
  enableScrollHide?: boolean;
  isPortfolioView?: boolean;
}

type ExpandedSection = 'product' | 'pricing' | null;

type NavbarBusiness = { id: string; name: string; favicon_url?: string | null };
type NavbarDraft = { draftId: string; displayName?: string | null; progress: number };

function BusinessTriggerLabel({
  businesses,
  selectedBusinessId,
  isPortfolioView,
  creating,
}: {
  businesses: NavbarBusiness[];
  selectedBusinessId: string | null;
  isPortfolioView: boolean;
  creating: boolean;
}) {
  const selected = businesses.find((business) => business.id === selectedBusinessId);
  if (isPortfolioView) {
    return (
      <>
        <Building2 className="h-4 w-4 shrink-0" />
        <span className="min-w-0 truncate">Select a Business</span>
      </>
    );
  }
  if (creating) {
    return (
      <>
        <Plus className="h-4 w-4 shrink-0" />
        <span className="min-w-0 truncate">Create Business</span>
      </>
    );
  }
  if (selected?.favicon_url) {
    return (
      <>
        <img src={selected.favicon_url} alt="" className="h-4 w-4 shrink-0 rounded-sm object-cover" />
        <span className="min-w-0 truncate">{selected.name}</span>
      </>
    );
  }
  return (
    <>
      <Building2 className="h-4 w-4 shrink-0" />
      <span className="min-w-0 truncate">{selected?.name || 'Select Business'}</span>
    </>
  );
}

function BusinessMenu({
  businesses,
  drafts,
  selectedBusinessId,
  isPortfolioView,
  creating,
  canCreate,
  showPortfolio,
  onSelect,
}: {
  businesses: NavbarBusiness[];
  drafts: NavbarDraft[];
  selectedBusinessId: string | null;
  isPortfolioView: boolean;
  creating: boolean;
  canCreate: boolean;
  showPortfolio: boolean;
  onSelect: (id: string) => void;
}) {
  return (
    <>
      <div className="mb-1 px-2 py-1 text-xs text-popover-foreground dark:text-card-foreground">Business</div>
      <ButtonGroup orientation="vertical" className="w-full">
        {showPortfolio && (
          <Button
            variant={isPortfolioView ? 'default' : 'outline'}
            size="sm"
            className="w-full justify-start"
            onClick={() => onSelect('all-businesses')}
          >
            <Building2 className="h-4 w-4" />
            All Businesses
          </Button>
        )}
        {businesses.map((business) => {
          const selected = !isPortfolioView && !creating && business.id === selectedBusinessId;
          return (
            <Button
              key={business.id}
              variant={selected ? 'default' : 'outline'}
              size="sm"
              className="w-full justify-start"
              onClick={() => onSelect(business.id)}
            >
              {business.favicon_url ? (
                <img src={business.favicon_url} alt="" className="h-4 w-4 rounded-sm object-cover" />
              ) : (
                <Building2 className="h-4 w-4" />
              )}
              <span className="truncate">{business.name}</span>
            </Button>
          );
        })}
        {drafts.map((draft) => (
          <Button
            key={draft.draftId}
            variant="outline"
            size="sm"
            className="w-full justify-start"
            onClick={() => onSelect(`draft-${draft.draftId}`)}
          >
            <RotateCcw className="h-4 w-4" />
            <span className="truncate">{draft.displayName || 'Untitled'} ({draft.progress}%)</span>
          </Button>
        ))}
        {canCreate && (
          <Button
            variant={creating ? 'default' : 'outline'}
            size="sm"
            className="w-full justify-start"
            onClick={() => onSelect('create-business')}
          >
            <Plus className="h-4 w-4" />
            Create Business
          </Button>
        )}
        <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => onSelect('manage-businesses')}>
          <Settings className="h-4 w-4" />
          Manage Businesses
        </Button>
      </ButtonGroup>
    </>
  );
}

const Navbar: React.FC<NavbarProps> = ({
  businesses = [],
  selectedBusinessId = null,
  onBusinessChange,
  loadingBusinesses = false,
  hideBusinessNavigation = false,
  forceShowBusinessSelect = false,
  enableScrollHide = false,
  isPortfolioView = false
}) => {
  const { user, isEmployee } = useAuth();
  const { employee, business } = useEmployee();
  const { isAccountSettingsOpen, openAccountSettings, closeAccountSettings } = useAccountSettings();
  const { isBusinessSettingsOpen, openBusinessSettings, closeBusinessSettings } = useBusinessSettings();
  const { unreadCount, systemNotificationCount, countByPriority, filter, notifications, markAllAsRead, dismissAll, clearSystemNotifications } = useNotificationContext();
  const { openPreferred, close: closeAI, viewMode } = useAIView();
  const { subscriptionSummary, currentPlan } = useSubscription();
  const isFreePlan = currentPlan?.name === 'free';

  const navigate = useNavigate();
  const location = useLocation();

  const handleAIToggle = () => {
    if (isFreePlan) {
      toast.error('AI is not included on the free plan');
      return;
    }
    if (location.pathname === '/create-business') {
      toast("Coming soon", { description: "AI Assistant will be available here soon." });
      return;
    }
    if (viewMode === 'hidden') {
      openPreferred();
    } else {
      closeAI();
    }
  };

  const goToPricing = () => {
    navigate('/pricing');
  };

  const goToHome = () => {
    navigate('/');
  };

  const isPricingActive = location.pathname === '/pricing';
  const { theme, setTheme } = useTheme();
  const isMobile = useIsMobile();
  const [expandedSection, setExpandedSection] = useState<ExpandedSection>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Mobile sheet state for animation
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [menuButtonPosition, setMenuButtonPosition] = useState({ top: 0, right: 0 });

  // Mobile business selector sheet state
  const [isMobileBusinessOpen, setIsMobileBusinessOpen] = useState(false);

  // Mobile notifications sheet state
  const [isMobileNotificationsOpen, setIsMobileNotificationsOpen] = useState(false);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);

  // Scroll-hide behavior state
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const lastScrollY = useRef(0);


  // Scroll-hide effect: hide on scroll down, show on scroll up
  useEffect(() => {
    if (!enableScrollHide) return;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollThreshold = 10;

      // Always show when near top of page
      if (currentScrollY < 100) {
        setIsNavbarVisible(true);
      } else if (currentScrollY > lastScrollY.current + scrollThreshold) {
        // Scrolling down - hide navbar
        setIsNavbarVisible(false);
      } else if (currentScrollY < lastScrollY.current - scrollThreshold) {
        // Scrolling up - show navbar
        setIsNavbarVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [enableScrollHide]);

  const handleLogout = async () => {
    try {
      const { error } = await signOut();
      if (error) throw error;
      toast.success("Logged out successfully!", { category: "Authentication" });
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Failed to log out", { category: "Authentication" });
    }
  };

  const handleAccountSettingsClick = () => {
    if (isAccountSettingsOpen) closeAccountSettings();
    else openAccountSettings();
  };

  const handleBusinessSettingsClick = () => {
    if (isBusinessSettingsOpen) closeBusinessSettings();
    else openBusinessSettings();
  };

  const isAccountSettingsActive = () => {
    return isAccountSettingsOpen;
  };

  const isBusinessSettingsActive = () => {
    return isBusinessSettingsOpen;
  };

  // Settings stays in this set so the button does not vanish once the route changes.
  const isOnDashboardPage = location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/employee-dashboard') ||
    location.pathname.startsWith('/portfolio') ||
    location.pathname.startsWith('/settings/');

  // Show business settings button when: user is logged in, on dashboard, and has a selected business
  const showBusinessSettingsButton = user && isOnDashboardPage && selectedBusinessId;

  const handleBusinessChange = (businessId: string) => {
    if (businessId === 'manage-businesses') {
      // Open account settings to plan management
      handleAccountSettingsClick();
      return;
    }

    if (businessId === 'create-business') {
      navigate('/create-business');
      return;
    }

    // Resume a specific draft
    if (businessId.startsWith('draft-')) {
      const draftId = businessId.slice(6);
      navigate(`/create-business?draft=${draftId}`);
      return;
    }

    if (businessId === 'all-businesses') {
      navigate('/portfolio');
      return;
    }

    if (onBusinessChange) {
      onBusinessChange(businessId);
    }

    const settingsPath = businessSettingsPath(location.pathname, location.search, businessId);
    if (settingsPath) {
      navigate(settingsPath);
      return;
    }

    // Navigate to dashboard when selecting a business from create-business page or portfolio
    if (location.pathname === '/create-business' || isPortfolioView) {
      navigate('/dashboard');
    }
    // Toast is shown by BusinessContext.switchBusiness
  };

  const handleSectionMouseEnter = (section: ExpandedSection) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // If switching between sections, close first then open new section
    if (expandedSection && section && expandedSection !== section) {
      setExpandedSection(null);
      setTimeout(() => {
        setExpandedSection(section);
      }, 350);
    } else {
      setExpandedSection(section);
    }
  };

  const handleSectionMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setExpandedSection(null);
    }, 200);
  };

  const publicPages = ['/', '/features', '/pricing', '/roadmap', '/changelog', '/blog', '/contact'];
  const isOnPublicPage = publicPages.includes(location.pathname);
  const showBusinessSelect = user && businesses.length > 0 && (!hideBusinessNavigation || forceShowBusinessSelect) && !isOnPublicPage;

  // Whether the user has remaining quota to start a brand-new business creation
  const venuesLimit = subscriptionSummary?.businesses_limit ?? null;
  const { summaries: navbarDrafts } = useWizardDrafts(user?.id);
  const showPortfolio = canOpenPortfolio(currentPlan?.name, businesses.length);
  const visibleDrafts = isFreePlan && businesses.length > 0 ? [] : navbarDrafts;
  const canCreateNewBusiness = isFreePlan
    ? businesses.length + visibleDrafts.length === 0
    : venuesLimit == null || (businesses.length + navbarDrafts.length) < venuesLimit;

  return (
    <>
      {/* Spacer to prevent content jump when navbar is fixed */}
      {enableScrollHide && <div className={isMobile ? "h-14" : "h-12"} />}
      <nav className={`${enableScrollHide ? 'fixed' : 'sticky'} top-0 left-0 right-0 z-50 bg-navbar shadow-sm border-b border-border transition-transform duration-300 overflow-visible ${
        enableScrollHide && !isNavbarVisible ? '-translate-y-full' : 'translate-y-0'
      } ${isMobile ? 'h-14' : 'h-12'}`}>
      <div className={`mx-auto flex items-center overflow-visible ${isMobile ? 'h-14 justify-between px-1.5' : 'h-12 justify-between px-2'}`}>
        {/* Left section */}
        <div className={`flex items-center min-w-0 ${isMobile ? '' : 'gap-2 flex-1'}`}>
          {isMobile && user && !hideBusinessNavigation && (
            <SidebarTrigger className="!h-8 !w-8" />
          )}
          {isMobile && hideBusinessNavigation && (
            <Button variant="ghost" onClick={goToHome} className="flex-shrink-0 flex items-center !h-auto !pl-1 !pr-0 !py-0 hover:bg-transparent">
              <img src="/media/tag-innovation-logo.png" alt="Tag Innovation Logo" className="h-5 w-auto" />
            </Button>
          )}

          {/* Desktop: Business select on left */}
          {!isMobile && (
            showBusinessSelect ? (
              <>
              {/* Show logo alongside business select when sidebar is hidden (e.g. create-business) */}
              {/* Matches sidebar: bg-sidebar, border-r, same width (12rem) and pl-3 */}
              {hideBusinessNavigation && (
                <Button variant="ghost" onClick={goToHome} className="flex-shrink-0 flex items-center w-[12rem] md:w-[12rem] !h-12 -ml-2 !pl-3 !pr-0 !py-0 bg-sidebar border-r border-sidebar-border hover:bg-sidebar rounded-none">
                  <img src="/media/tag-innovation-logo.png" alt="Tag Innovation Logo" className="h-6 w-auto" />
                  <span className="ml-2 text-sm font-semibold">TagInn</span>
                </Button>
              )}
              {showPortfolio && (
              <SimpleTooltip content="Portfolio">
                <Button
                  variant={isPortfolioView ? 'default' : 'outline'}
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => navigate('/portfolio')}
                  data-onboarding-id="portfolio-button"
                >
                  <Building2 className="h-4 w-4" />
                  <span className="sr-only">Portfolio</span>
                </Button>
              </SimpleTooltip>
              )}
              <HoverCard openDelay={0} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={loadingBusinesses}
                    className="min-w-[120px] max-w-[200px] justify-start px-2"
                    data-onboarding-id="business-selector"
                  >
                    <BusinessTriggerLabel
                      businesses={businesses}
                      selectedBusinessId={selectedBusinessId}
                      isPortfolioView={isPortfolioView}
                      creating={location.pathname === '/create-business'}
                    />
                    <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
                  </Button>
                </HoverCardTrigger>
                <HoverCardContent align="start" className="max-h-[70vh] w-56 overflow-y-auto p-1 bg-background">
                  <BusinessMenu
                    businesses={businesses}
                    drafts={visibleDrafts}
                    selectedBusinessId={selectedBusinessId}
                    isPortfolioView={isPortfolioView}
                    creating={location.pathname === '/create-business'}
                    canCreate={canCreateNewBusiness}
                    showPortfolio={showPortfolio}
                    onSelect={handleBusinessChange}
                  />
                </HoverCardContent>
              </HoverCard>
              </>
            ) : isEmployee && employee && location.pathname === '/employee-portal' ? (
              // Show employee info instead of logo for logged-in employees on employee portal page only
              <div className="flex items-center gap-2 text-sm min-w-0">
                <span className="font-medium truncate">{employee.name}</span>
                <span className="text-muted-foreground truncate">{business?.name || 'Loading...'}</span>
              </div>
            ) : (
              <Button variant="ghost" onClick={goToHome} className="flex-shrink-0 flex items-center !h-auto !p-0 hover:bg-transparent">
                <img src="/media/tag-innovation-logo.png" alt="Tag Innovation Logo" className="h-6 pl-1 w-auto" />
                <span className="ml-2 text-sm font-semibold">TagInn</span>
              </Button>
            )
          )}
          {/* Navigation Links - Desktop only */}
          <nav className="hidden sm:flex items-center shrink-0">
            {!hideBusinessNavigation && (!user || isOnPublicPage) ? (
              <>
                <NavLinkWithExpand
                  section="product"
                  expandedSection={expandedSection}
                  onMouseEnter={handleSectionMouseEnter}
                  onMouseLeave={handleSectionMouseLeave}
                  onClick={() => navigate('/features')}
                  isActive={['/features', '/roadmap', '/changelog'].includes(location.pathname)}
                >
                  Product
                </NavLinkWithExpand>
                <Button
                  variant="ghost"
                  onClick={goToPricing}
                  className={`inline-flex items-center !h-auto !px-1 !pt-1 !pb-0 rounded-none border-b-2 text-sm md:text-sm font-medium transition-colors hover:bg-transparent ${
                    isPricingActive ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Pricing
                </Button>
              </>
            ) : !hideBusinessNavigation ? (
              <>
                {!isEmployee && (
                  <ButtonGroup aria-label="Dashboard navigation" data-onboarding-id="dashboard-switcher">
                    <Button
                      variant={location.pathname === '/dashboard' ? 'default' : 'outline'}
                      size="sm"
                      className="h-8 px-2 min-[900px]:px-3 text-xs"
                      onClick={() => navigate('/dashboard')}
                    >
                      <Star className="h-3.5 w-3.5 min-[900px]:mr-1.5" />
                      <span className="hidden min-[900px]:inline">Customers</span>
                    </Button>
                    <Button
                      variant={location.pathname === '/employee-dashboard' ? 'default' : 'outline'}
                      size="sm"
                      className="h-8 px-2 min-[900px]:px-3 text-xs"
                      onClick={() => navigate('/employee-dashboard')}
                    >
                      <Users className="h-3.5 w-3.5 min-[900px]:mr-1.5" />
                      <span className="hidden min-[900px]:inline">Team</span>
                    </Button>
                  </ButtonGroup>
                )}
              </>
            ) : null}
          </nav>
        </div>

        {/* Center section */}
        {isMobile ? (
          /* Mobile: Business selector centered - uses bottom sheet */
          user && showBusinessSelect ? (
            <div className="flex-1 flex justify-center px-2">
              <Sheet open={isMobileBusinessOpen} onOpenChange={setIsMobileBusinessOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={loadingBusinesses}
                    className="max-w-[180px] justify-start px-2"
                  >
                    <BusinessTriggerLabel
                      businesses={businesses}
                      selectedBusinessId={selectedBusinessId}
                      isPortfolioView={isPortfolioView}
                      creating={location.pathname === '/create-business'}
                    />
                    <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="bottom" className="max-h-[70dvh] overflow-y-auto rounded-t-xl bg-background p-1">
                  <BusinessMenu
                    businesses={businesses}
                    drafts={visibleDrafts}
                    selectedBusinessId={selectedBusinessId}
                    isPortfolioView={isPortfolioView}
                    creating={location.pathname === '/create-business'}
                    canCreate={canCreateNewBusiness}
                    showPortfolio={showPortfolio}
                    onSelect={(id) => { handleBusinessChange(id); setIsMobileBusinessOpen(false); }}
                  />
                </SheetContent>
              </Sheet>
            </div>
          ) : !user ? (
            <Button variant="ghost" onClick={goToHome} className="flex-1 flex justify-start items-center !h-auto !pl-2 !pr-0 !py-0 hover:bg-transparent">
              <img src="/media/tag-innovation-logo.png" alt="Tag Innovation Logo" className="h-8 w-8 sm:h-6 sm:w-auto" />
              <span className="ml-2 text-sm font-semibold hidden sm:inline">TagInn</span>
            </Button>
          ) : isEmployee && employee && location.pathname === '/employee-portal' ? (
            // Show employee info for mobile instead of logo - stacked vertically on employee portal page only
            <div className="flex-1 flex justify-start items-center gap-1 text-sm min-w-0">
              <div className="flex flex-col items-start">
                <span className="font-semibold truncate px-1">{employee.name}</span>
                <span className="text-muted-foreground truncate px-1 text-xs">{business?.name || '...'}</span>
              </div>
            </div>
          ) : null
        ) : null}

        {/* Right section */}
        <div className={`flex items-center overflow-visible ${isMobile ? '' : 'gap-2'}`}>
          {!isMobile && (
            <>
              {user ? (
                <>
                  {!isOnDashboardPage && !hideBusinessNavigation && !isEmployee && (
                    <Button variant="default" size="sm" asChild>
                      <Link to="/dashboard">
                        Dashboard
                      </Link>
                    </Button>
                  )}
                  {showBusinessSettingsButton && (
                    <BusinessSettingsHoverMenu isSettingsOpen={isBusinessSettingsOpen}>
                      <Button
                        variant={isBusinessSettingsActive() ? "default" : "outline"}
                        size="sm"
                        onClick={handleBusinessSettingsClick}
                        data-onboarding-id="business-settings"
                      >
                        <Settings className="h-4 w-4" />
                        <span className="sr-only">Business Settings</span>
                      </Button>
                    </BusinessSettingsHoverMenu>
                  )}
                  <AccountSettingsHoverMenu isSettingsOpen={isAccountSettingsOpen} onLogout={handleLogout} hidePlanButton={isEmployee}>
                    <Button
                      variant={isAccountSettingsActive() ? "default" : "outline"}
                      size="sm"
                      onClick={handleAccountSettingsClick}
                    >
                      <User className="h-4 w-4" />
                      <span className="sr-only">Account Settings</span>
                    </Button>
                  </AccountSettingsHoverMenu>
                  {/* AI Chat Button */}
                  {!isFreePlan && <SimpleTooltip content={viewMode === 'hidden' ? "Open AI Assistant" : "Close AI Assistant"}>
                    <Button
                      variant={viewMode !== 'hidden' ? "default" : "outline"}
                      size="icon"
                      className="h-8 w-8"
                      onClick={handleAIToggle}
                      data-onboarding-id="ai-assistant"
                    >
                      <Bot className="h-4 w-4" />
                      <span className="sr-only">AI Assistant</span>
                    </Button>
                  </SimpleTooltip>}
                </>
              ) : (
                  <>
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/auth">Log in</Link>
                    </Button>
                    <Button size="sm" asChild>
                      <Link to="/auth?mode=sign-up">Sign up</Link>
                    </Button>
                    {/* AI Chat Button - for non-logged-in users */}
                    <SimpleTooltip content={viewMode === 'hidden' ? "Open AI Assistant" : "Close AI Assistant"}>
                      <Button
                        variant={viewMode !== 'hidden' ? "default" : "outline"}
                        size="icon"
                        className="h-8 w-8"
                        onClick={handleAIToggle}
                      >
                        <Bot className="h-4 w-4" />
                        <span className="sr-only">AI Assistant</span>
                      </Button>
                    </SimpleTooltip>
                  </>
                )}

              {user && <NotificationCenter />}

              <HoverCard openDelay={0} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                    <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                    <span className="sr-only">Toggle theme</span>
                  </Button>
                </HoverCardTrigger>
                <HoverCardContent align="end" className="w-32 p-1 bg-background">
                  <div className="mb-1 px-2 py-1 text-xs text-popover-foreground dark:text-card-foreground">Theme</div>
                  <ButtonGroup orientation="vertical" className="w-full">
                    <Button
                      variant={theme === 'light' ? 'default' : 'outline'}
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => setTheme('light')}
                    >
                      <Sun className="h-4 w-4" />
                      Light
                    </Button>
                    <Button
                      variant={theme === 'dark' ? 'default' : 'outline'}
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => setTheme('dark')}
                    >
                      <Moon className="h-4 w-4" />
                      Dark
                    </Button>
                    <Button
                      variant={theme === 'system' ? 'default' : 'outline'}
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => setTheme('system')}
                    >
                      <Monitor className="h-4 w-4" />
                      System
                    </Button>
                  </ButtonGroup>
                </HoverCardContent>
              </HoverCard>
            </>
          )}

          {isMobile && (
            <div className="flex items-center gap-2">
              {/* Mobile: Get Started button for non-logged-in users */}
              {!user && (
                <Button size="sm" className="h-8 px-3 text-xs" asChild>
                  <Link to="/auth?mode=sign-up">
                    Get Tag Inn
                  </Link>
                </Button>
              )}

              {/* Mobile Notifications Sheet - only for logged-in users */}
              {user && (
              <Sheet
                open={isMobileNotificationsOpen}
                onOpenChange={setIsMobileNotificationsOpen}
              >
                <SheetTrigger asChild>
                  <Button
                    ref={notificationButtonRef}
                    variant="outline"
                    size="icon"
                    className="relative h-8 w-8 overflow-visible p-0"
                  >
                    <Bell className={cn(
                      'h-4 w-4 transition-colors',
                      countByPriority.urgent > 0 && 'text-danger',
                    )} />
                    {/* Unread indicator dot */}
                    {unreadCount + systemNotificationCount > 0 && (
                      <span
                        className={cn(
                          'absolute top-0.5 right-0.5 block h-2 w-2 rounded-full ring-1 ring-background',
                          countByPriority.urgent > 0 ? 'bg-red-500' :
                          countByPriority.action_required > 0 ? 'bg-amber-500' :
                          'bg-primary'
                        )}
                      />
                    )}
                    {/* Pulse animation for urgent */}
                    {countByPriority.urgent > 0 && (
                      <span className="absolute top-0.5 right-0.5 block h-2 w-2 rounded-full animate-ping bg-danger opacity-75" />
                    )}
                    <span className="sr-only">View notifications</span>
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="bottom"
                  className="top-auto max-h-[70dvh] overflow-y-auto bg-background p-1"
                >
                  <div className="mb-1 px-2 py-1 text-xs text-popover-foreground dark:text-card-foreground">Notifications</div>
                  <NotificationFilters />
                  <div className="my-1">
                    <NotificationList />
                  </div>
                  {(filter === 'system' ? systemNotificationCount > 0 : notifications.length > 0) && (
                    filter === 'system' ? (
                      <ButtonGroup orientation="vertical" className="w-full">
                        <Button variant="outline" size="sm" className="w-full justify-start" onClick={() => clearSystemNotifications()}>
                          <Trash2 className="h-4 w-4" />
                          Clear {systemNotificationCount}
                        </Button>
                      </ButtonGroup>
                    ) : (
                      <ButtonGroup orientation="vertical" className="w-full">
                        <Button variant="outline" size="sm" className="w-full justify-start" onClick={markAllAsRead} disabled={unreadCount === 0}>
                          <CheckCheck className="h-4 w-4" />
                          Mark all read
                        </Button>
                        <Button variant="outline" size="sm" className="w-full justify-start" onClick={dismissAll}>
                          <Trash2 className="h-4 w-4" />
                          Clear all
                        </Button>
                      </ButtonGroup>
                    )
                  )}
                </SheetContent>
              </Sheet>
              )}

              {/* Mobile Menu Sheet */}
              <Sheet
                open={isMobileMenuOpen}
                onOpenChange={(open) => {
                  if (open && menuButtonRef.current) {
                    const rect = menuButtonRef.current.getBoundingClientRect();
                    setMenuButtonPosition({ top: rect.top, right: window.innerWidth - rect.right });
                  }
                  setIsMobileMenuOpen(open);
                }}
              >
                <SheetTrigger asChild>
                  <Button
                    ref={menuButtonRef}
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 p-0"
                  >
                    {isMobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
                    <span className="sr-only">{isMobileMenuOpen ? 'Close menu' : 'Open menu'}</span>
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="bottom"
                  className="h-[calc(100dvh-3rem)] overflow-y-auto top-12 p-0 bg-background"
                  hideDefaultClose
                  style={{
                    '--menu-button-top': `${menuButtonPosition.top}px`,
                    '--menu-button-right': `${menuButtonPosition.right}px`,
                  } as React.CSSProperties}
                >
                  {user ? (
                    <MobileMenuPageContent
                      onClose={() => setIsMobileMenuOpen(false)}
                      onLogout={handleLogout}
                      businesses={businesses}
                    />
                  ) : !hideBusinessNavigation ? (
                    <div className="flex flex-col h-full bg-background">
                      {/* Navigation links */}
                      <nav className="flex flex-col px-6 pt-6">
                        {[
                          { to: '/features', label: 'Features' },
                          { to: '/roadmap', label: 'Roadmap' },
                          { to: '/changelog', label: 'Changelog' },
                          { to: '/pricing', label: 'Pricing', isButton: true },
                        ].map((item) => {
                          const isActive = item.to === location.pathname;
                          const commonClass = cn(
                            'block w-full text-left rounded-lg px-3 py-3.5 text-xl font-medium transition-colors',
                            isActive ? 'text-primary' : 'text-foreground hover:bg-muted'
                          );
                          return item.isButton ? (
                            <Button
                              key={item.to}
                              variant="ghost"
                              onClick={() => { navigate(item.to); setIsMobileMenuOpen(false); }}
                              className={cn(commonClass, "h-auto md:h-auto px-3 md:px-3 py-3.5 md:py-3.5 text-xl md:text-xl", isActive && "hover:bg-transparent")}
                            >
                              {item.label}
                            </Button>
                          ) : (
                            <Link
                              key={item.to}
                              to={item.to}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={commonClass}
                            >
                              {item.label}
                            </Link>
                          );
                        })}
                      </nav>

                      {/* Bottom section: auth + theme toggle */}
                      <div className="mt-auto px-6 pb-6 space-y-4">
                        <div className="space-y-2">
                          <Button variant="outline" className="w-full h-12 text-base" asChild>
                            <Link to="/auth" onClick={() => setIsMobileMenuOpen(false)}>Log in</Link>
                          </Button>
                          <Button className="w-full h-12 text-base" asChild>
                            <Link to="/auth?mode=sign-up" onClick={() => setIsMobileMenuOpen(false)}>Sign up</Link>
                          </Button>
                        </div>

                        <ButtonGroup className="w-full">
                          <Button
                            variant="outline"
                            size="default"
                            className={cn(
                              'flex-1 justify-center',
                              theme === 'light' && 'bg-primary text-primary-foreground hover:bg-primary/90'
                            )}
                            onClick={() => setTheme('light')}
                          >
                            <Sun className="h-4 w-4" />
                            <span className="ml-2">Light</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="default"
                            className={cn(
                              'flex-1 justify-center',
                              theme === 'dark' && 'bg-primary text-primary-foreground hover:bg-primary/90'
                            )}
                            onClick={() => setTheme('dark')}
                          >
                            <Moon className="h-4 w-4" />
                            <span className="ml-2">Dark</span>
                          </Button>
                        </ButtonGroup>
                      </div>
                    </div>
                  ) : null}
                </SheetContent>
              </Sheet>
            </div>
          )}
        </div>
      </div>

      {/* Expanding Panel - Overlays on top of content */}
      <div
        className={`hidden sm:block absolute left-0 right-0 top-full bg-navbar shadow-lg border-b border-border overflow-hidden ${
          expandedSection
            ? 'max-h-96 opacity-100 translate-y-0 pointer-events-auto'
            : 'max-h-0 opacity-0 -translate-y-0 pointer-events-none'
        }`}
        style={{
          transition: 'max-height 350ms cubic-bezier(0.4, 0, 0.2, 1), opacity 350ms cubic-bezier(0.4, 0, 0.2, 1), transform 350ms cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: expandedSection ? 'max-height, opacity, transform' : 'auto',
        }}
        onMouseEnter={() => {
          // Only trigger if there's an actual section (prevent interference during closing)
          if (expandedSection) {
            handleSectionMouseEnter(expandedSection);
          }
        }}
        onMouseLeave={handleSectionMouseLeave}
      >
        {expandedSection === 'product' && <ProductExpandedContent navigate={navigate} />}
        {expandedSection === 'pricing' && <PricingExpandedContent navigate={navigate} />}
      </div>
    </nav>
    </>
  );
};

// NavLink with expand functionality
interface NavLinkWithExpandProps {
  section: ExpandedSection;
  expandedSection: ExpandedSection;
  onMouseEnter: (section: ExpandedSection) => void;
  onMouseLeave: () => void;
  onClick: () => void;
  isActive: boolean;
  children: React.ReactNode;
}

const NavLinkWithExpand: React.FC<NavLinkWithExpandProps> = ({
  section,
  expandedSection,
  onMouseEnter,
  onMouseLeave,
  onClick,
  isActive,
  children,
}) => {
  const linkClasses = `inline-flex items-center gap-1 px-1 pt-1 border-b-2 text-sm font-medium transition-colors cursor-pointer ${
    isActive ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
  }`;

  const isExpanded = expandedSection === section;

  return (
    <span
      className={linkClasses}
      onMouseEnter={() => onMouseEnter(section)}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {children}
      <CollapseChevron open={isExpanded} className="text-current" />
    </span>
  );
};

// Simple NavLink without expand
const SimpleNavLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  const linkClasses = `inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors ${
    isActive ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
  }`;

  return (
    <Link to={to} className={linkClasses}>
      {children}
    </Link>
  );
};

// Product expanded content (Features, Roadmap, Changelog)
const ProductExpandedContent = ({ navigate }: { navigate: (path: string) => void }) => {
  const productLinks = [
    {
      name: "Features",
      description: "Everything TagInn can do for your business",
      icon: Layers,
      link: "/features",
    },
    {
      name: "Roadmap",
      description: "See what we're building next",
      icon: Map,
      link: "/roadmap",
    },
    {
      name: "Changelog",
      description: "Latest updates and improvements",
      icon: FileText,
      link: "/changelog",
    },
  ];

  return (
    <div className="bg-navbar">
      <div className="mx-auto px-8 py-5">
        <div className="flex items-center justify-center gap-2">
          {productLinks.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.link}
                className="group flex items-center gap-3 rounded-lg px-4 py-2.5 transition-colors hover:bg-muted/50"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-foreground">
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                    {item.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Pricing expanded content
const PricingExpandedContent = ({ navigate }: { navigate: (path: string) => void }) => {
  return (
    <div className="bg-navbar">
      <div className="max-w-3xl mx-auto px-8 py-8">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">Simple, Transparent Pricing</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Start on the free plan, or choose Growth, Management, or Complete.
            </p>
          </div>
          <Button
            variant="default"
            size="sm"
            onClick={() => navigate('/pricing')}
            className="ml-4"
          >
            View Pricing
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const MobileNavLink = ({ to, scrollTo, children }: { to?: string, scrollTo?: string, children: React.ReactNode }) => {
  const location = useLocation();
  const isActive = to ? location.pathname === to : false;

  if (scrollTo) {
    return (
      <span
        onClick={() => document.getElementById(scrollTo)?.scrollIntoView({ behavior: 'smooth' })}
        className="w-full text-lg font-semibold transition-colors text-foreground hover:text-primary cursor-pointer"
      >
        {children}
      </span>
    );
  }

  return (
    <Link
      to={to!}
      className={`w-full text-lg font-semibold transition-colors ${isActive ? 'text-primary' : 'text-foreground hover:text-primary'}`}>
      {children}
    </Link>
  );
}

export default Navbar;
